/**
 * One-call connect entry point for 1auth.
 *
 * `createOneAuthConnection` is the ergonomic front door for integrators who
 * want a single function that:
 *   1. opens the 1auth dialog so the user picks **1auth passkey** *or* a
 *      **traditional wallet** (WalletConnect / injected), and
 *   2. hands back a ready-to-use EIP-1193 provider plus the connected
 *      `{ address, signerType }`.
 *
 * When the user picks a traditional wallet (an EOA), the returned provider
 * forwards `eth_sendTransaction` / `personal_sign` / `signTypedData` /
 * `wallet_sendCalls` straight to that wallet's native RPC — **no Rhinestone
 * intents**. When they pick a passkey, transactions route through 1auth intents
 * exactly as before. The app writes one downstream code path either way.
 *
 * This is purely additive: `OneAuthClient.connect()` and
 * `createOneAuthProvider()` are unchanged and remain the lower-level building
 * blocks this factory composes.
 *
 * @module
 */

import { OneAuthClient } from "./client";
import { createOneAuthProvider, type OneAuthProvider } from "./provider";
import type {
  CloseOnStatus,
  PasskeyProviderConfig,
  SignerType,
  ThemeConfig,
} from "./types";

/**
 * Default chain the returned provider reports and uses as the intent target
 * when a send omits an explicit chain. Base mainnet.
 */
const DEFAULT_CHAIN_ID = 8453;

/**
 * Provider-shaping options that apply on top of the passkey client config.
 * Mirrors the relevant {@link createOneAuthProvider} options.
 */
export interface OneAuthConnectionOptions {
  /**
   * Chain the returned provider reports via `eth_chainId` and uses as the
   * default intent target. Defaults to `8453` (Base). Ignored for EOA sessions
   * beyond the reported chain id — an EOA transacts on whatever chain its
   * wallet is on.
   */
  defaultChainId?: number;
  /**
   * localStorage key the session is persisted under. Defaults to `"1auth-user"`.
   * Override only to run multiple independent connections on one origin.
   */
  storageKey?: string;
  /** Forwarded to the provider for passkey intent sends. */
  closeOn?: CloseOnStatus;
  waitForHash?: boolean;
  hashTimeoutMs?: number;
  hashIntervalMs?: number;
  /**
   * Opt in to offering a **traditional wallet as the account signer**. When
   * `true`, the dialog's wallet path connects the chosen EOA as the signer (a
   * `signerType: "eoa"` session whose transactions bypass Rhinestone intents).
   *
   * **Defaults to `false`** — by default the app gets 1auth smart accounts +
   * intents, and the wallet path (if the user picks it) still bootstraps a
   * passkey account. Passkey selection is unaffected either way. Can be
   * overridden per call via {@link OneAuthConnectOptions.eoaConnect}.
   */
  eoaConnect?: boolean;
}

/** Full config form: constructs the client for you. Only `clientId` is practically required. */
export type OneAuthConnectionConfig = PasskeyProviderConfig &
  OneAuthConnectionOptions;

/** Reuse form: bring your own configured {@link OneAuthClient}. */
export type OneAuthConnectionConfigWithClient = {
  client: OneAuthClient;
} & OneAuthConnectionOptions;

/** Options accepted by {@link OneAuthConnection.connect}. */
export interface OneAuthConnectOptions {
  theme?: ThemeConfig;
  /** Applies only to the auth-modal fallback path (email/OAuth entry). */
  oauthEnabled?: boolean;
  /**
   * Per-call override of {@link OneAuthConnectionOptions.eoaConnect}. Defaults
   * to the connection's config value (which itself defaults to `false`).
   */
  eoaConnect?: boolean;
}

/**
 * The resolved session. Discriminated on `signerType` so callers can branch on
 * EOA vs passkey with full type safety.
 */
export interface OneAuthSession {
  /** `"eoa"` = traditional wallet (plain tx, no intents). `"passkey"` = 1auth smart account (intents). */
  signerType: SignerType;
  address: `0x${string}`;
  /** The same {@link OneAuthProvider} instance exposed on the connection. */
  provider: OneAuthProvider;
  /** Chain id the provider reports (see {@link OneAuthConnectionOptions.defaultChainId}). */
  chainId: number;
  /** True when a returning user was reconnected with no visible UI. */
  autoConnected: boolean;
}

/** Error thrown by {@link OneAuthConnection.connect} when the user cancels or auth fails. */
export interface OneAuthConnectError {
  code: string;
  message: string;
}

/** The handle returned by {@link createOneAuthConnection}. */
export interface OneAuthConnection {
  /** The underlying client — escape hatch for `signMessage`, history, the account dialog, etc. */
  client: OneAuthClient;
  /** Ready-to-use EIP-1193 provider. Stable across reconnects. */
  provider: OneAuthProvider;
  /**
   * Open the 1auth dialog so the user picks passkey OR a traditional wallet.
   * Binds the resulting session to {@link OneAuthConnection.provider} before it
   * resolves, so a send issued immediately after routes correctly.
   *
   * @throws {@link OneAuthConnectError} shape (`{ code, message }`) on cancel/failure.
   */
  connect: (options?: OneAuthConnectOptions) => Promise<OneAuthSession>;
  /** Current session read from persisted state, or `null`. Never opens a dialog. */
  getSession: () => OneAuthSession | null;
  /** Clear the session and emit `accountsChanged([])` / `disconnect`. */
  disconnect: () => Promise<void>;
  /**
   * Subscribe to session changes (connect/disconnect). Returns an unsubscribe
   * function. Sugar over `provider.on("accountsChanged", …)`.
   */
  subscribe: (listener: (session: OneAuthSession | null) => void) => () => void;
}

/**
 * Reads the persisted session for this connection's storage key. Kept in this
 * module (rather than reaching into the provider) so `getSession` works without
 * a prior `connect()` — e.g. on a page reload where the app rehydrates.
 */
function readStoredSession(
  storageKey: string,
  provider: OneAuthProvider,
  chainId: number,
): OneAuthSession | null {
  if (typeof window === "undefined") return null;
  try {
    // Bare `localStorage` (not `window.localStorage`) to match provider.ts and
    // client.ts — in a browser they are identical, and it keeps the storage
    // access consistent across the SDK.
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      address?: `0x${string}`;
      signerType?: SignerType;
    };
    if (!parsed?.address) return null;
    return {
      // Absent signerType = legacy passkey session (backwards compatible).
      signerType: parsed.signerType === "eoa" ? "eoa" : "passkey",
      address: parsed.address,
      provider,
      chainId,
      autoConnected: false,
    };
  } catch {
    return null;
  }
}

/**
 * Create a one-call 1auth connection.
 *
 * @example Zero-boilerplate
 * ```typescript
 * import { createOneAuthConnection } from "@rhinestone/1auth";
 * import { createWalletClient, custom } from "viem";
 * import { base } from "viem/chains";
 *
 * const conn = createOneAuthConnection({ clientId: "my-app" });
 * const session = await conn.connect(); // user picks passkey OR wallet
 *
 * const walletClient = createWalletClient({
 *   account: session.address,
 *   chain: base,
 *   transport: custom(session.provider),
 * });
 * // Same call for both signer types; EOA bypasses intents, passkey uses them.
 * await walletClient.sendTransaction({ to: "0x…", value: 1n });
 * ```
 *
 * @example Reuse an existing client
 * ```typescript
 * const client = new OneAuthClient({ clientId: "my-app", sponsorship });
 * const conn = createOneAuthConnection({ client, defaultChainId: 8453 });
 * const { address, signerType, provider } = await conn.connect();
 * ```
 */
export function createOneAuthConnection(
  config: OneAuthConnectionConfig,
): OneAuthConnection;
export function createOneAuthConnection(
  config: OneAuthConnectionConfigWithClient,
): OneAuthConnection;
export function createOneAuthConnection(
  config: OneAuthConnectionConfig | OneAuthConnectionConfigWithClient,
): OneAuthConnection {
  const {
    defaultChainId,
    storageKey: storageKeyOption,
    closeOn,
    waitForHash,
    hashTimeoutMs,
    hashIntervalMs,
    eoaConnect: eoaConnectConfig,
    ...clientConfig
  } = config as OneAuthConnectionConfig & OneAuthConnectionConfigWithClient;

  // Reuse a supplied client, or construct one from the passkey config.
  const client =
    "client" in config && config.client
      ? config.client
      : new OneAuthClient(clientConfig as PasskeyProviderConfig);

  const chainId = defaultChainId ?? DEFAULT_CHAIN_ID;
  const storageKey = storageKeyOption ?? "1auth-user";

  const provider = createOneAuthProvider({
    client,
    chainId,
    storageKey,
    closeOn,
    waitForHash,
    hashTimeoutMs,
    hashIntervalMs,
  });

  const getSession = (): OneAuthSession | null =>
    readStoredSession(storageKey, provider, chainId);

  const connect = async (
    options?: OneAuthConnectOptions,
  ): Promise<OneAuthSession> => {
    // Wallet-as-signer is opt-in (default false → smart accounts + intents).
    // Per-call option wins over the connection's config default.
    const eoaConnect = options?.eoaConnect ?? eoaConnectConfig ?? false;
    const result = await client.connect({ ...options, eoaConnect });
    if (!result.success || !result.user?.address) {
      const error: OneAuthConnectError = result.error ?? {
        code: "USER_CANCELLED",
        message: "Connection was cancelled",
      };
      throw Object.assign(new Error(error.message), { code: error.code });
    }

    const signerType: SignerType =
      result.signerType === "eoa" ? "eoa" : "passkey";

    // Bind before returning so a transaction issued in the same tick routes to
    // the wallet (EOA) rather than falling back to passkey intents.
    provider.setSession({ address: result.user.address, signerType });

    return {
      signerType,
      address: result.user.address,
      provider,
      chainId,
      autoConnected: result.autoConnected ?? false,
    };
  };

  const disconnect = async (): Promise<void> => {
    await provider.disconnect();
  };

  const subscribe = (
    listener: (session: OneAuthSession | null) => void,
  ): (() => void) => {
    const onAccountsChanged = (...args: unknown[]) => {
      const accounts = args[0] as unknown[] | undefined;
      listener(
        accounts && accounts.length > 0 ? getSession() : null,
      );
    };
    provider.on("accountsChanged", onAccountsChanged);
    return () => provider.removeListener("accountsChanged", onAccountsChanged);
  };

  return { client, provider, connect, getSession, disconnect, subscribe };
}
